import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

const STATUS_MESSAGES = [
  { icon: '📸', text: 'Reading your photo...' },
  { icon: '👕', text: 'Analyzing your outfit...' },
  { icon: '🎨', text: 'Drawing your design...' },
  { icon: '🧱', text: 'Building your figure...' },
  { icon: '✨', text: 'Adding final touches...' },
  { icon: '🎉', text: 'Almost done!' }
];

export const ProcessingView = ({ preview, progress, message, onCancel }) => {
  const [displayMessage, setDisplayMessage] = useState(STATUS_MESSAGES[0]);
  const [animationPhase, setAnimationPhase] = useState(0);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Update display message based on progress
  useEffect(() => {
    if (progress < 15) {
      setDisplayMessage(STATUS_MESSAGES[0]);
    } else if (progress < 30) {
      setDisplayMessage(STATUS_MESSAGES[1]);
    } else if (progress < 50) {
      setDisplayMessage(STATUS_MESSAGES[2]);
    } else if (progress < 70) {
      setDisplayMessage(STATUS_MESSAGES[3]);
    } else if (progress < 90) {
      setDisplayMessage(STATUS_MESSAGES[4]);
    } else {
      setDisplayMessage(STATUS_MESSAGES[5]);
    }
  }, [progress]);

  // Animate building blocks
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationPhase((prev) => (prev + 1) % 4);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4 text-center">
          LaLala Model Maker
        </h1>
      </div>

      <div className="bg-white rounded-2xl shadow-xl p-8">
        {/* Thumbnail Bar */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <button
            onClick={() => setShowPreviewModal(true)}
            className="flex items-center space-x-3 hover:bg-gray-50 rounded-lg p-2 transition-colors"
          >
            <img
              src={preview}
              alt="Your photo"
              className="h-12 w-12 rounded-lg object-cover cursor-pointer"
            />
            <span className="text-sm text-gray-600">Tap to view</span>
          </button>
          <button
            onClick={onCancel}
            className="flex items-center space-x-2 px-4 py-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Cancel job"
          >
            <X className="w-5 h-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Cancel</span>
          </button>
        </div>

        {/* Main Content */}
        <div className="space-y-8 py-8">
          {/* Magic Message */}
          <div className="text-center">
            <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500 mb-2">
              ✨ Creating magic... ✨
            </h2>
          </div>

          {/* Progress Bar */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-lg font-medium text-gray-700">Progress</span>
              <span className="text-2xl font-bold text-orange-500">
                {Math.round(progress)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
              <div
                className="h-6 rounded-full transition-all duration-500 bg-gradient-to-r from-orange-400 to-red-500"
                style={{ width: `${Math.min(progress, 100)}%` }}
              >
                <div className="w-full h-full bg-gradient-to-r from-white/0 via-white/30 to-white/0 animate-shimmer"></div>
              </div>
            </div>
          </div>

          {/* Status Message */}
          <div className="text-center py-4">
            <div className="inline-flex items-center space-x-3 bg-orange-50 px-6 py-4 rounded-2xl">
              <span className="text-4xl animate-bounce">{displayMessage.icon}</span>
              <span className="text-xl font-medium text-gray-700">
                {message || displayMessage.text}
              </span>
            </div>
          </div>

          {/* Animated Building Blocks */}
          <div className="flex justify-center items-center space-x-2 py-4">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`w-6 h-6 rounded transition-all duration-300 ${
                  animationPhase === i
                    ? 'bg-orange-500 scale-125'
                    : 'bg-gray-300 scale-100'
                }`}
                style={{
                  animationDelay: `${i * 0.1}s`
                }}
              />
            ))}
          </div>

          {/* Estimated Time */}
          <div className="text-center">
            <p className="text-sm text-gray-500">
              This usually takes about 60-90 seconds
            </p>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setShowPreviewModal(false)}
        >
          <button
            onClick={() => setShowPreviewModal(false)}
            className="absolute top-4 right-4 z-50 p-3 bg-white/10 hover:bg-white/20
              rounded-full transition-colors backdrop-blur-sm"
            title="Close"
          >
            <X className="w-8 h-8 text-white" />
          </button>

          <div className="absolute top-4 left-4 z-50 bg-black/50 px-6 py-3 rounded-2xl backdrop-blur-sm">
            <h3 className="text-white text-xl font-bold">Your Original Photo</h3>
          </div>

          <div className="relative w-full h-full flex items-center justify-center p-4">
            <img
              src={preview}
              alt="Your original photo"
              className="max-w-full max-h-[80vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
};
